import { addVariantToCart } from "../cart/add";
import { openPreferredCartDrawer } from "../cart/open-preferred-drawer";
import { bindQuickAddCtaButtons } from "../product-card/quick-add-cta";
import { trapFocusInContainer } from "./accessibility";
import {
  getMatchingVariant,
  getUniqueOptionValues,
  type QuickViewOption,
  type QuickViewProduct,
  type QuickViewVariant
} from "./variant-selector";

interface AddToCartButtonState {
  closeQuickViewAfterAdd?: boolean;
}

type QuickViewFocusable = HTMLElement | null;

interface PredictiveSearchProductResult {
  handle?: string;
  url?: string;
  title?: string;
}

interface PredictiveSearchResponse {
  resources?: {
    results?: {
      products?: PredictiveSearchProductResult[];
    };
  };
}

export class QuickAddSheet {
  private quickViewDrawer: HTMLElement | null;
  private quickViewContent: HTMLElement | null;
  private closeButton: HTMLButtonElement | null;
  private lastFocusedElement: QuickViewFocusable = null;
  private openRequestController: AbortController | null = null;
  private openRequestToken = 0;
  private moneyFormat = "${{amount}}";
  private readonly lowStockThreshold = 5;
  private readonly linkedColorCache = new Map<string, QuickViewProduct[]>();

  constructor() {
    this.quickViewDrawer = document.getElementById("quick-view-drawer");
    this.quickViewContent = document.getElementById("quick-view-content");
    this.closeButton = document.getElementById("close-quick-view") as HTMLButtonElement | null;

    const themeMoneyFormat = window.theme?.moneyFormat;
    if (typeof themeMoneyFormat === "string" && themeMoneyFormat.length > 0) {
      this.moneyFormat = themeMoneyFormat;
    }

    this.init();
  }

  private init(): void {
    if (!this.quickViewDrawer || !this.quickViewContent || !this.closeButton) {
      return;
    }

    this.bindButtons(document);

    this.closeButton.addEventListener("click", () => this.closeQuickView());

    this.quickViewDrawer.addEventListener("click", (event) => {
      if (event.target === this.quickViewDrawer) {
        this.closeQuickView();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (!this.quickViewDrawer?.classList.contains("open")) return;

      if (event.key === "Escape") {
        this.closeQuickView();
        return;
      }

      if (event.key === "Tab" && this.quickViewDrawer) {
        trapFocusInContainer(event, this.quickViewDrawer);
      }
    });

    document.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const addToCartButton = target.closest(
        "#quick-view-content .add-to-cart-button"
      ) as HTMLButtonElement | null;

      if (!addToCartButton) return;

      event.preventDefault();
      const variantId = addToCartButton.dataset.id;
      if (!variantId) return;
      void this.addToCart(variantId, addToCartButton, {
        closeQuickViewAfterAdd: true
      });
    });
  }

  public bindButtons(container: ParentNode): void {
    if (!container) return;

    const quickViewButtons = container.querySelectorAll<HTMLButtonElement>(".quick-view-button");
    for (const button of quickViewButtons) {
      if (button.dataset.quickViewBound === "1" || button.disabled) continue;

      button.dataset.quickViewBound = "1";
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        const productHandle = button.dataset.productHandle;
        if (!productHandle) return;

        this.openQuickView(productHandle);
      });
    }

    bindQuickAddCtaButtons(container, {
      onQuickAdd: (variantId, button) => {
        void this.addToCart(variantId, button);
      }
    });
  }

  private abortOpenRequest(): void {
    if (!this.openRequestController) return;
    this.openRequestController.abort();
    this.openRequestController = null;
  }

  public closeQuickView(): void {
    if (!this.quickViewDrawer) return;

    this.abortOpenRequest();
    this.openRequestToken += 1;

    this.quickViewDrawer.classList.remove("open");
    this.quickViewDrawer.classList.remove("quick-view-drawer--mobile");
    document.body.classList.remove("overflow-hidden");

    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === "function") {
      this.lastFocusedElement.focus();
    }
    this.lastFocusedElement = null;
  }

  private setInitialFocus(): void {
    if (this.closeButton && typeof this.closeButton.focus === "function") {
      this.closeButton.focus();
    }
  }

  private formatMoney(cents: number, format = this.moneyFormat): string {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const matched = format.match(placeholderRegex);

    const withDelimiters = (
      amount: number,
      precision: number,
      thousands: string,
      decimal: string
    ): string => {
      const numberString = (amount / 100).toFixed(precision);
      const parts = numberString.split(".");
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const fraction = parts[1] ? `${decimal}${parts[1]}` : "";
      return `${whole}${fraction}`;
    };

    if (!matched) {
      return withDelimiters(cents, 2, ",", ".");
    }

    const value = (() => {
      switch (matched[1]) {
        case "amount":
          return withDelimiters(cents, 2, ",", ".");
        case "amount_no_decimals":
          return withDelimiters(cents, 0, ",", ".");
        case "amount_with_comma_separator":
          return withDelimiters(cents, 2, ".", ",");
        case "amount_no_decimals_with_comma_separator":
          return withDelimiters(cents, 0, ".", ",");
        default:
          return withDelimiters(cents, 2, ",", ".");
      }
    })();

    return format.replace(placeholderRegex, value);
  }

  private openCartDrawer(): void {
    openPreferredCartDrawer();
  }

  public async addToCart(
    variantId: string | number,
    button: HTMLButtonElement,
    options: AddToCartButtonState = {}
  ): Promise<void> {
    if (!variantId || !button) return;

    const isIconQuickAdd = button.classList.contains("quick-view-button--icon");
    if (!isIconQuickAdd && !button.dataset.defaultLabel) {
      button.dataset.defaultLabel = button.textContent?.trim() ?? "Add to Cart";
    }

    button.disabled = true;
    if (isIconQuickAdd) {
      button.classList.add("is-adding");
      button.setAttribute("aria-busy", "true");
    } else {
      button.textContent = "Adding...";
    }

    try {
      const parsedMaxPerCart = Number.parseInt(button.dataset.maxPerCart ?? "", 10);
      const maxPerCart = Number.isFinite(parsedMaxPerCart) && parsedMaxPerCart > 0
        ? parsedMaxPerCart
        : undefined;

      await addVariantToCart(variantId, {
        quantity: 1,
        maxPerCart
      });

      button.disabled = false;
      button.classList.remove("is-adding");
      button.removeAttribute("aria-busy");
      if (!isIconQuickAdd) {
        button.textContent = button.dataset.defaultLabel ?? "Add to Cart";
      }

      if (options.closeQuickViewAfterAdd) {
        this.closeQuickView();
      }

      document.querySelector(".nudge-offer")?.remove();
      document.body.dispatchEvent(
        new CustomEvent("baseline:modalcart:afteradditem", {
          bubbles: true,
          detail: { source: "quick-add-sheet" }
        })
      );
      document.dispatchEvent(
        new CustomEvent("baseline:modalcart:afteradditem", {
          detail: { source: "quick-add-sheet" }
        })
      );
      this.openCartDrawer();

      const upsellProto = (
        window as Window & {
          InCartUpsell?: { prototype?: { findOfferWhenReady?: () => void } };
        }
      ).InCartUpsell?.prototype;

      if (upsellProto && typeof upsellProto.findOfferWhenReady === "function") {
        upsellProto.findOfferWhenReady();
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      button.disabled = false;
      button.classList.remove("is-adding");
      button.removeAttribute("aria-busy");
      if (!isIconQuickAdd) {
        button.textContent = button.dataset.defaultLabel ?? "Add to Cart";
      }
    }
  }

  private getProductJsonUrls(handle: string): string[] {
    const rawHandle = String(handle).trim();
    if (!rawHandle) return [];

    const routeRoot = window.Shopify?.routes?.root ?? "/";
    const normalizedRoot = routeRoot.endsWith("/") ? routeRoot : `${routeRoot}/`;
    const handleCandidates = [encodeURIComponent(rawHandle), rawHandle];
    const urls: string[] = [];

    for (const candidateHandle of handleCandidates) {
      const marketScopedPath = `${normalizedRoot}products/${candidateHandle}.js`;
      const rootPath = `/products/${candidateHandle}.js`;
      urls.push(this.withCurrencyParam(marketScopedPath));
      urls.push(this.withCurrencyParam(rootPath));
      urls.push(marketScopedPath);
      urls.push(rootPath);
    }

    return urls.filter((url, index) => urls.indexOf(url) === index);
  }

  private async fetchProduct(handle: string, signal: AbortSignal): Promise<QuickViewProduct> {
    const urls = this.getProductJsonUrls(handle);
    if (!urls.length) {
      throw new Error("Missing product handle");
    }

    let lastError: unknown = null;
    for (const url of urls) {
      try {
        const response = await fetch(url, { credentials: "same-origin", signal });
        if (!response.ok) {
          throw new Error(`Failed to load product JSON (${response.status})`);
        }
        return (await response.json()) as QuickViewProduct;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          throw error;
        }
        lastError = error;
      }
    }

    throw lastError ?? new Error("Failed to load quick view product");
  }

  private getSizedImageUrl(url: string, width: number): string {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set("width", String(width));
      return parsed.toString();
    } catch {
      return url;
    }
  }

  private escapeHtml(value: string | number | null | undefined): string {
    if (value === null || value === undefined) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  private getOptionLabelText(
    optionLabel: string | QuickViewOption,
    optionIndex: number
  ): string {
    if (typeof optionLabel === "string" && optionLabel.trim().length > 0) {
      return optionLabel;
    }

    if (
      optionLabel &&
      typeof optionLabel === "object" &&
      typeof optionLabel.name === "string" &&
      optionLabel.name.trim().length > 0
    ) {
      return optionLabel.name;
    }

    return `Option ${optionIndex + 1}`;
  }

  private getActiveCurrencyCode(): string | null {
    const urlCurrency = new URL(window.location.href).searchParams.get("currency");
    if (urlCurrency && urlCurrency.trim()) {
      return urlCurrency.trim().toUpperCase();
    }

    const headerCurrencyToggle = document.querySelector<HTMLElement>(
      "#shopify-section-header [data-disclosure-country] [data-disclosure-toggle]"
    );
    if (headerCurrencyToggle?.textContent) {
      const headerCurrencyMatch = headerCurrencyToggle.textContent
        .trim()
        .toUpperCase()
        .match(/\b[A-Z]{3}\b/);
      if (headerCurrencyMatch?.[0]) {
        return headerCurrencyMatch[0];
      }
    }

    const activeCurrency = window.Shopify?.currency?.active;
    if (typeof activeCurrency === "string" && activeCurrency.trim()) {
      return activeCurrency.trim().toUpperCase();
    }

    return null;
  }

  private withCurrencyParam(url: string): string {
    const activeCurrency = this.getActiveCurrencyCode();
    if (!activeCurrency) {
      return url;
    }

    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set("currency", activeCurrency);
      return url.startsWith("/") ? `${parsed.pathname}${parsed.search}` : parsed.toString();
    } catch {
      return url;
    }
  }

  private getColorOptionIndex(product: QuickViewProduct): number {
    if (!Array.isArray(product.options)) {
      return -1;
    }

    return product.options.findIndex((optionLabel, optionIndex) => {
      const normalizedLabel = this.getOptionLabelText(optionLabel, optionIndex).trim().toLowerCase();
      return normalizedLabel.includes("color") || normalizedLabel.includes("colour");
    });
  }

  private getColorOptionValue(product: QuickViewProduct): string | null {
    const colorOptionIndex = this.getColorOptionIndex(product);
    if (colorOptionIndex < 0) {
      return null;
    }

    const values = getUniqueOptionValues(product, colorOptionIndex);
    if (values.length !== 1) {
      return null;
    }

    const colorValue = values[0]?.trim();
    return colorValue || null;
  }

  private shouldLimitToSingleQuantity(
    product: QuickViewProduct & {
      product_type?: string;
      tags?: string[] | string;
    }
  ): boolean {
    const title = String(product.title || "").toLowerCase();
    if (title.includes("vintage")) {
      return true;
    }

    const productType = String(product.product_type || "").toLowerCase();
    if (productType.includes("vintage")) {
      return true;
    }

    const rawTags = product.tags;
    const tags = Array.isArray(rawTags)
      ? rawTags
      : typeof rawTags === "string"
        ? rawTags.split(",")
        : [];

    return tags.some((tag) => String(tag).toLowerCase().includes("vintage"));
  }

  private deriveSiblingBaseTitle(product: QuickViewProduct): string {
    const title = String(product.title || "").trim();
    if (!title) {
      return "";
    }

    const colorValue = this.getColorOptionValue(product);
    if (!colorValue) {
      return title;
    }

    const escapedColor = colorValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const colorSuffixPattern = new RegExp(`\\s*[\\-–]\\s*${escapedColor}$`, "i");
    if (colorSuffixPattern.test(title)) {
      return title.replace(colorSuffixPattern, "").trim();
    }

    return title;
  }

  private normalizeSiblingTitle(title: string): string {
    return String(title || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  }

  private extractProductHandleFromUrl(url: string): string | null {
    const match = String(url || "").match(/\/products\/([^/?#]+)/i);
    return match?.[1] ?? null;
  }

  private async fetchPredictiveProductHandles(
    query: string,
    signal: AbortSignal
  ): Promise<string[]> {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return [];
    }

    const routeRoot = window.Shopify?.routes?.root ?? "/";
    const normalizedRoot = routeRoot.endsWith("/") ? routeRoot : `${routeRoot}/`;
    const endpoint = new URL(`${normalizedRoot}search/suggest.json`, window.location.origin);
    endpoint.searchParams.set("q", trimmedQuery);
    endpoint.searchParams.set("resources[type]", "product");
    endpoint.searchParams.set("resources[limit]", "12");
    endpoint.searchParams.set("resources[options][unavailable_products]", "last");

    const response = await fetch(this.withCurrencyParam(endpoint.toString()), {
      credentials: "same-origin",
      signal
    });
    if (!response.ok) {
      throw new Error(`Failed to load predictive search (${response.status})`);
    }

    const payload = (await response.json()) as PredictiveSearchResponse;
    const products = payload.resources?.results?.products ?? [];

    const handles = new Set<string>();
    for (const product of products) {
      if (typeof product.handle === "string" && product.handle.trim()) {
        handles.add(product.handle.trim());
        continue;
      }

      const handleFromUrl = this.extractProductHandleFromUrl(product.url || "");
      if (handleFromUrl) {
        handles.add(handleFromUrl);
      }
    }

    return Array.from(handles);
  }

  private async fetchLinkedColorProducts(
    product: QuickViewProduct,
    signal: AbortSignal
  ): Promise<QuickViewProduct[]> {
    const colorOptionIndex = this.getColorOptionIndex(product);
    if (colorOptionIndex < 0) {
      return [];
    }

    const colorValues = getUniqueOptionValues(product, colorOptionIndex);
    if (colorValues.length !== 1) {
      return [];
    }

    const baseTitle = this.deriveSiblingBaseTitle(product);
    const normalizedBaseTitle = this.normalizeSiblingTitle(baseTitle);
    if (!normalizedBaseTitle) {
      return [];
    }

    const cacheKey = `${normalizedBaseTitle}::${this.getActiveCurrencyCode() || ""}`;
    const cached = this.linkedColorCache.get(cacheKey);
    if (cached && cached.length > 1) {
      return cached;
    }

    const siblingHandles = await this.fetchPredictiveProductHandles(baseTitle, signal);
    const uniqueHandles = new Set<string>(siblingHandles);
    uniqueHandles.add(product.handle);

    const linkedProductsByHandle = new Map<string, QuickViewProduct>();
    linkedProductsByHandle.set(product.handle, product);

    for (const siblingHandle of uniqueHandles) {
      if (!siblingHandle || siblingHandle === product.handle) {
        continue;
      }

      try {
        const siblingProduct = await this.fetchProduct(siblingHandle, signal);
        const siblingBaseTitle = this.normalizeSiblingTitle(this.deriveSiblingBaseTitle(siblingProduct));
        if (siblingBaseTitle !== normalizedBaseTitle) {
          continue;
        }

        const siblingColorIndex = this.getColorOptionIndex(siblingProduct);
        if (siblingColorIndex < 0) {
          continue;
        }

        linkedProductsByHandle.set(siblingProduct.handle, siblingProduct);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          throw error;
        }
      }
    }

    const linkedProducts = Array.from(linkedProductsByHandle.values()).sort((left, right) =>
      left.title.localeCompare(right.title)
    );

    this.linkedColorCache.set(cacheKey, linkedProducts);
    return linkedProducts.length > 1 ? linkedProducts : [];
  }

  private bindLinkedColorButtons(): void {
    if (!this.quickViewContent) {
      return;
    }

    const currentHandle = this.quickViewContent.dataset.productHandle || "";
    const linkedColorButtons =
      this.quickViewContent.querySelectorAll<HTMLButtonElement>("[data-linked-product-handle]");

    for (const button of linkedColorButtons) {
      if (button.dataset.linkedColorBound === "1") {
        continue;
      }

      button.dataset.linkedColorBound = "1";
      button.addEventListener("click", (event) => {
        event.preventDefault();
        const targetHandle = button.dataset.linkedProductHandle;
        if (!targetHandle || targetHandle === currentHandle) {
          return;
        }

        this.openQuickView(targetHandle);
      });
    }
  }

  public openQuickView(productHandle: string): void {
    const handle = String(productHandle || "").trim();
    if (!handle || !this.quickViewDrawer || !this.quickViewContent) return;

    this.lastFocusedElement = document.activeElement as HTMLElement | null;
    this.abortOpenRequest();

    const requestToken = ++this.openRequestToken;

    this.quickViewContent.innerHTML =
      '<p class="quick-view-loading" role="status" aria-live="polite">Loading quick add...</p>';

    this.quickViewDrawer.classList.toggle(
      "quick-view-drawer--mobile",
      window.matchMedia("(max-width: 989px)").matches
    );
    this.quickViewDrawer.classList.add("open");
    document.body.classList.add("overflow-hidden");
    this.setInitialFocus();

    this.openRequestController = new AbortController();
    const requestSignal = this.openRequestController.signal;

    void this.fetchProduct(handle, requestSignal)
      .then(async (product) => {
        if (requestToken !== this.openRequestToken) return;
        if (!product || !Array.isArray(product.variants) || product.variants.length === 0) {
          throw new Error("Quick view product data is incomplete");
        }

        let linkedColorProducts: QuickViewProduct[] = [];
        try {
          linkedColorProducts = await this.fetchLinkedColorProducts(product, requestSignal);
        } catch (error) {
          if (error instanceof DOMException && error.name === "AbortError") {
            throw error;
          }
        }
        if (requestToken !== this.openRequestToken) return;

        const selectedOptions: Array<string | null> = new Array(product.options.length).fill(null);
        const optionGroupsHtml = this.buildOptionGroups(
          product,
          selectedOptions,
          linkedColorProducts
        );
        const mediaHtml = this.buildMediaHtml(product);
        const productUrl = product.url || `/products/${product.handle}`;
        const safeTitle = this.escapeHtml(product.title);
        const safeProductUrl = this.escapeHtml(productUrl);

        const hasCompareAtPrice =
          typeof product.compare_at_price === "number" && product.compare_at_price > product.price;
        const salePrice = hasCompareAtPrice
          ? this.formatMoney(product.compare_at_price as number)
          : "";
        const regularPrice = this.formatMoney(product.price);
        const maxPerCartAttr = this.shouldLimitToSingleQuantity(product) ? ' data-max-per-cart="1"' : "";

        this.quickViewContent!.innerHTML = `
          <div class="quick-view-product">
            ${mediaHtml}
            <h2 class="quick-view-title">${safeTitle}</h2>
            <s class="block lg:inline quick-view-cprice">${salePrice}</s>
            <span class="quick-view-price">${regularPrice}</span>
            ${optionGroupsHtml}
            <div class="quick_atc_btn_wrp">
              <p class="quick-view-stock-note" data-quick-view-stock-note></p>
              <button class="add-to-cart-button" data-id="" data-default-label="Add to Cart"${maxPerCartAttr}>Add to Cart</button>
            </div>
            <a href="${safeProductUrl}" class="view_full_pdct">See full details</a>
          </div>
        `;
        this.quickViewContent!.dataset.productHandle = product.handle;

        this.initializeSlider();
        this.handleVariantSelection(product, selectedOptions);
        this.bindLinkedColorButtons();
        this.setInitialFocus();
      })
      .catch((error) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        if (requestToken !== this.openRequestToken) return;

        console.error("Error fetching product:", error);
        this.quickViewContent!.innerHTML =
          '<p class="quick-view-error" role="status">Unable to load quick add. Please try again.</p>';
      })
      .finally(() => {
        if (requestToken === this.openRequestToken) {
          this.openRequestController = null;
        }
      });
  }

  private buildOptionGroups(
    product: QuickViewProduct,
    selectedOptions: Array<string | null>,
    linkedColorProducts: QuickViewProduct[] = []
  ): string {
    if (!Array.isArray(product.options) || product.options.length === 0) {
      return "";
    }

    let html = "";
    const colorOptionIndex = this.getColorOptionIndex(product);
    const showLinkedColorOptions = colorOptionIndex >= 0 && linkedColorProducts.length > 1;

    if (showLinkedColorOptions) {
      const colorButtonsHtml = linkedColorProducts
        .map((linkedProduct) => {
          const linkedColorValue = this.getColorOptionValue(linkedProduct) || linkedProduct.title;
          const safeColorValue = this.escapeHtml(linkedColorValue);
          const safeHandle = this.escapeHtml(linkedProduct.handle);
          const isSelected = linkedProduct.handle === product.handle;

          return `<button type="button" class="quick-view-option ${isSelected ? "selected" : ""}" data-linked-product-handle="${safeHandle}" aria-pressed="${isSelected ? "true" : "false"}">${safeColorValue}</button>`;
        })
        .join("");

      html += `
        <div class="quick-view-option-group quick-view-option-group--linked-colors">
          <span class="quick-view-option-label">Color:</span>
          <div class="quick-view-options" data-option-index="${colorOptionIndex}">
            ${colorButtonsHtml}
          </div>
        </div>
      `;
    }

    product.options.forEach((optionLabel, optionIndex) => {
      const optionLabelText = this.getOptionLabelText(optionLabel, optionIndex);
      const uniqueValues = getUniqueOptionValues(product, optionIndex);
      selectedOptions[optionIndex] = uniqueValues[0] ?? null;

      if (showLinkedColorOptions && optionIndex === colorOptionIndex && uniqueValues.length <= 1) {
        return;
      }

      const valuesHtml = uniqueValues
        .map((value, index) => {
          const safeValue = this.escapeHtml(value);
          const selectedClass = index === 0 ? "selected" : "";
          const pressed = index === 0 ? "true" : "false";
          return `<button type="button" class="quick-view-option ${selectedClass}" data-value="${safeValue}" aria-pressed="${pressed}">${safeValue}</button>`;
        })
        .join("");

      html += `
        <div class="quick-view-option-group">
          <span class="quick-view-option-label">${this.escapeHtml(optionLabelText)}:</span>
          <div class="quick-view-options" data-option-index="${optionIndex}">
            ${valuesHtml}
          </div>
        </div>
      `;
    });

    return html;
  }

  private buildMediaHtml(product: QuickViewProduct): string {
    if (!Array.isArray(product.images) || product.images.length === 0) {
      return "";
    }

    const imageAlt = this.escapeHtml(product.title);
    const slides = product.images
      .map((image, index) => {
        const image480 = this.getSizedImageUrl(image, 480);
        const image720 = this.getSizedImageUrl(image, 720);
        const image960 = this.getSizedImageUrl(image, 960);
        return `
          <div class="slider-slide ${index === 0 ? "active" : ""}">
            <img
              src="${image960}"
              srcset="${image480} 480w, ${image720} 720w, ${image960} 960w"
              sizes="(max-width: 989px) 84vw, 420px"
              alt="${imageAlt}"
              class="slider-image"
              loading="${index === 0 ? "eager" : "lazy"}"
            >
          </div>
        `;
      })
      .join("");

    return `
      <div class="quick-view-slider">
        <div class="slider-wrapper">${slides}</div>
      </div>
    `;
  }

  private handleVariantSelection(
    product: QuickViewProduct,
    selectedOptions: Array<string | null>
  ): void {
    if (!this.quickViewContent) return;

    const optionGroups = this.quickViewContent.querySelectorAll<HTMLElement>(".quick-view-options");
    const addToCartButton = this.quickViewContent.querySelector<HTMLButtonElement>(
      ".add-to-cart-button"
    );
    const priceEl = this.quickViewContent.querySelector<HTMLElement>(".quick-view-price");
    const comparePriceEl = this.quickViewContent.querySelector<HTMLElement>(".quick-view-cprice");
    const stockNoteEl =
      this.quickViewContent.querySelector<HTMLElement>("[data-quick-view-stock-note]");

    if (!addToCartButton) return;

    const updateStockNote = (variant?: QuickViewVariant): void => {
      if (!stockNoteEl) return;
      stockNoteEl.classList.remove("is-visible");
      stockNoteEl.textContent = "";

      if (!variant || !variant.available) return;

      const quantity = Number(variant.inventory_quantity);
      const hasTrackedInventory = Boolean(variant.inventory_management);
      const canOversell = variant.inventory_policy === "continue";
      if (!hasTrackedInventory || canOversell || !Number.isFinite(quantity)) return;
      if (quantity <= 0 || quantity > this.lowStockThreshold) return;

      stockNoteEl.textContent =
        quantity === 1 ? "Only 1 left in stock" : `Only ${quantity} left in stock`;
      stockNoteEl.classList.add("is-visible");
    };

    const syncVariantState = (variant?: QuickViewVariant): void => {
      if (!variant) {
        addToCartButton.dataset.id = "";
        addToCartButton.disabled = true;
        addToCartButton.textContent = "Sold Out";
        updateStockNote(undefined);
        return;
      }

      addToCartButton.dataset.id = String(variant.id);
      addToCartButton.disabled = !variant.available;
      addToCartButton.textContent = variant.available ? "Add to Cart" : "Sold Out";
      addToCartButton.dataset.defaultLabel = "Add to Cart";
      updateStockNote(variant);

      if (priceEl) {
        priceEl.textContent = this.formatMoney(variant.price);
      }
      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
        } else {
          comparePriceEl.textContent = "";
        }
      }
    };

    syncVariantState(getMatchingVariant(product, selectedOptions));

    for (const group of optionGroups) {
      group.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const selectedOption = target.closest(".quick-view-option") as HTMLButtonElement | null;
        if (!selectedOption || !group.contains(selectedOption)) return;

        const optionIndex = Number(group.dataset.optionIndex);
        if (!Number.isFinite(optionIndex)) return;

        const selectedValue = selectedOption.dataset.value;
        if (!selectedValue) return;

        group.querySelectorAll<HTMLButtonElement>(".quick-view-option").forEach((option) => {
          option.classList.remove("selected");
          option.setAttribute("aria-pressed", "false");
        });
        selectedOption.classList.add("selected");
        selectedOption.setAttribute("aria-pressed", "true");

        selectedOptions[optionIndex] = selectedValue;
        syncVariantState(getMatchingVariant(product, selectedOptions));
      });
    }
  }

  private initializeSlider(): void {
    if (!this.quickViewContent) return;

    const slider = this.quickViewContent.querySelector<HTMLElement>(".quick-view-slider");
    const sliderWrapper = slider?.querySelector<HTMLElement>(".slider-wrapper");
    const slides = sliderWrapper?.querySelectorAll<HTMLElement>(".slider-slide") ?? [];
    const dots = this.quickViewContent.querySelectorAll<HTMLButtonElement>(".quick-view-slider-dot");

    if (!slider || !sliderWrapper || slides.length === 0) {
      return;
    }

    const setActiveDot = (index: number): void => {
      dots.forEach((dot, idx) => {
        const isActive = idx === index;
        dot.classList.toggle("is-active", isActive);
        dot.classList.toggle("is-inactive", !isActive);
        if (isActive) {
          dot.setAttribute("aria-current", "true");
        } else {
          dot.removeAttribute("aria-current");
        }
      });
    };

    const isMobileQuickView = window.matchMedia("(max-width: 989px)").matches;
    let currentIndex = 0;

    if (isMobileQuickView) {
      slides.forEach((slide) => slide.classList.add("active"));
      setActiveDot(0);

      sliderWrapper.addEventListener(
        "scroll",
        () => {
          const scrollLeft = sliderWrapper.scrollLeft;
          let nearestIndex = 0;
          let nearestDistance = Number.POSITIVE_INFINITY;

          slides.forEach((slide, idx) => {
            const distance = Math.abs(slide.offsetLeft - scrollLeft);
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestIndex = idx;
            }
          });

          if (nearestIndex !== currentIndex) {
            currentIndex = nearestIndex;
            setActiveDot(currentIndex);
          }
        },
        { passive: true }
      );

      dots.forEach((dot) => {
        dot.addEventListener("click", (event) => {
          event.preventDefault();
          const targetIndex = Number(dot.dataset.slideIndex);
          if (!Number.isFinite(targetIndex) || !slides[targetIndex]) return;
          sliderWrapper.scrollTo({ left: slides[targetIndex].offsetLeft, behavior: "smooth" });
        });
      });
      return;
    }

    const showDesktopSlide = (index: number): void => {
      slides.forEach((slide, idx) => {
        slide.classList.toggle("active", idx === index);
      });
      setActiveDot(index);
    };

    dots.forEach((dot) => {
      dot.addEventListener("click", (event) => {
        event.preventDefault();
        const targetIndex = Number(dot.dataset.slideIndex);
        if (!Number.isFinite(targetIndex)) return;
        currentIndex = Math.max(0, Math.min(targetIndex, slides.length - 1));
        showDesktopSlide(currentIndex);
      });
    });

    showDesktopSlide(0);
  }
}
